import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CatalogCombobox } from './CatalogCombobox';
import { CLINICAS_OTRO_CIRUJANO, useListaClinica } from '../../lib/listasClinica';

/**
 * ParteDialog — Phase 27.8
 *
 * Crea/edita un PARTE de quirófano del Registro QX: el equipo del día
 * (cirujano, anestesista, clínica de procedencia, lugar, instrumentista,
 * circulante) se define UNA VEZ aquí y todas las intervenciones del parte
 * lo heredan. Al editar, el backend propaga los cambios a las
 * intervenciones ya registradas.
 *
 * Cirujano OBLIGATORIO y SOLO de la lista de cirujanos dados de alta
 * (dirección, 2026-09-08, punto 5): el parte se usa para facturar el tiempo
 * de quirófano a los cirujanos visitantes y, con texto libre, se escribía
 * la clínica en vez del cirujano o quedaba vacío. Aquí no hay
 * texto libre: si acude un cirujano o anestesista que no está en la lista,
 * se le da de alta desde el propio desplegable con el formato obligatorio
 * «Dr./Dra. Nombre Apellido1 Apellido2» + nº de colegiado (punto 7,
 * AltaProfesionalDialog) y queda en el desplegable para la próxima vez. El
 * backend lo valida igualmente (422).
 *
 * Registro de externos (dirección, 2026-09-08, punto 6): este parte es el
 * ORIGEN del informe de facturación a externos, así que:
 *   - la clínica de procedencia es OBLIGATORIA y de lista cerrada (Catálogo
 *     QX) con la opción explícita «Ninguna» para el cirujano que no viene
 *     derivado de ninguna clínica (así no se deja en blanco por descuido ni
 *     se escribe la misma clínica de tres formas);
 *   - si la clínica es una de las que lo admiten (Catálogo de cirugías →
 *     «Clínicas que admiten otro cirujano», V2: antes era una clínica escrita
 *     aquí) y el cirujano no es uno de los de la lista, se puede escribir
 *     «otro» cirujano a mano (obligatorio en ese caso), que cuenta como
 *     externo automáticamente;
 *   - debajo del cirujano se dice si es externo (sale en el Registro de
 *     externos) o colaborador de la clínica (no sale).
 *
 * Todos los campos son OBLIGATORIOS salvo las notas (administración, 2026-09-09,
 * punto 8): un parte a medias deja el informe de externos sin lugar, sin
 * anestesista o sin instrumentista, que es justo lo que hay que facturar. El
 * instrumentista y el circulante salen del catálogo como los demás (antes el
 * instrumentista era una lista fija con «enfermería» y «Externo», las
 * entradas genéricas que administración pidió quitar, punto 3).
 *
 * Props: { open, onOpenChange, initial (parte|null), defaultDate, onSaved }
 */
export const CLINICA_NINGUNA = 'Ninguna';

/**
 * Punto 8 (administración, 2026-09-09): «TODOS los campos del registro de quirofano
 * obligatorios para guardar, salvo Notas». El cirujano y la clínica tienen sus
 * propios avisos (son de lista cerrada); estos son el resto, en el orden en
 * que están en el formulario. Mismos mensajes que el backend
 * (services/qx_externos.mensaje_campo_obligatorio), que vuelve a validarlo.
 */
export const CAMPOS_OBLIGATORIOS_PARTE = [
  ['date', 'la fecha del parte'],
  ['lugar', 'el lugar de la operación'],
  ['anestesista', 'el anestesista'],
  ['instrumentista', 'el instrumentista'],
  ['circulante', 'el circulante'],
];

export const mensajeCampoObligatorio = (etiqueta) =>
  `Indica ${etiqueta}: en el parte de quirófano son obligatorios todos los campos salvo las notas`;

const _norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const EMPTY = (defaultDate) => ({
  date: defaultDate || new Date().toISOString().slice(0, 10),
  cirujano: '',
  cirujano_otro: false,
  clinica_origen: '',
  lugar: '',
  anestesista: '',
  instrumentista: '',
  circulante: '',
  notas: '',
});

export default function ParteDialog({ open, onOpenChange, initial, defaultDate, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => EMPTY(defaultDate));
  const [saving, setSaving] = useState(false);
  // Lista cerrada de clínicas y fichas de cirujanos (para decir si es externo).
  const [clinicas, setClinicas] = useState([]);
  const [cirujanos, setCirujanos] = useState([]);
  // Las clínicas con las que vale un «otro cirujano» escrito a mano.
  const [conOtroCirujano] = useListaClinica(CLINICAS_OTRO_CIRUJANO);
  const admiteOtro = (clinica) => !!clinica
    && conOtroCirujano.some((c) => _norm(c?.nombre) === _norm(clinica));

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      date: initial.date || '',
      cirujano: initial.cirujano || '',
      cirujano_otro: !!initial.cirujano_otro,
      clinica_origen: initial.clinica_origen || '',
      lugar: initial.lugar || '',
      anestesista: initial.anestesista || '',
      instrumentista: initial.instrumentista || '',
      circulante: initial.circulante || '',
      notas: initial.notas || '',
    } : EMPTY(defaultDate));
    setSaving(false);
    api.get('/api/qx-catalogo', { params: { tipo: 'clinica_origen' } })
      .then((r) => setClinicas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setClinicas([]));
    api.get('/api/qx-catalogo', { params: { tipo: 'cirujano' } })
      .then((r) => setCirujanos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCirujanos([]));
  }, [open, initial, defaultDate]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const conOtro = admiteOtro(form.clinica_origen);
  const otroActivo = conOtro && form.cirujano_otro;
  const enLista = (nombre) => clinicas.some((c) => _norm(c.nombre) === _norm(nombre));
  // El valor de un parte antiguo que no está en la lista se enseña para no
  // perderlo, pero el backend pedirá elegir uno de la lista al guardar.
  const clinicaFueraDeLista = form.clinica_origen && _norm(form.clinica_origen) !== _norm(CLINICA_NINGUNA) && !enLista(form.clinica_origen);

  const fichaCirujano = useMemo(
    () => (otroActivo ? null : cirujanos.find((c) => _norm(c.nombre) === _norm(form.cirujano)) || null),
    [cirujanos, form.cirujano, otroActivo],
  );

  const cambiarClinica = (v) => {
    setForm((f) => {
      const next = { ...f, clinica_origen: v };
      if (!admiteOtro(v) && f.cirujano_otro) {
        // Con una clínica que no lo admite no hay «otro cirujano»: se vuelve
        // a la lista.
        next.cirujano_otro = false;
        next.cirujano = '';
      }
      return next;
    });
  };

  const cambiarOtro = (activo) => {
    setForm((f) => ({ ...f, cirujano_otro: !!activo, cirujano: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (otroActivo) {
      if (!form.cirujano.trim()) {
        toast.error(`Escribe el nombre del otro cirujano de ${form.clinica_origen}`);
        return;
      }
    } else if (!form.cirujano.trim()) {
      toast.error('Elige el cirujano del parte de la lista');
      return;
    }
    if (!form.clinica_origen.trim()) {
      toast.error('Elige la clínica de procedencia (o «Ninguna» si no viene derivado de ninguna)');
      return;
    }
    const falta = CAMPOS_OBLIGATORIOS_PARTE.find(([campo]) => !(form[campo] || '').trim());
    if (falta) {
      toast.error(mensajeCampoObligatorio(falta[1]));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, cirujano_otro: otroActivo };
      if (isEdit) {
        await api.put(`/api/qx-partes/${initial.id}`, payload);
        toast.success('Parte actualizado (aplicado a sus intervenciones)');
      } else {
        await api.post('/api/qx-partes', payload);
        toast.success('Parte de quirófano creado');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo guardar el parte'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="parte-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar parte de quirófano' : 'Nuevo parte de quirófano'}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          El equipo se define una vez por parte; todas sus intervenciones lo heredan.
          Todos los campos son obligatorios salvo las notas.
        </p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Fecha <span className="text-red-600">*</span></Label>
            <Input
              type="date"
              required
              value={form.date}
              onChange={(e) => setF('date', e.target.value)}
              data-testid="parte-fecha"
            />
          </div>
          <div>
            <Label>Clínica de procedencia <span className="text-red-600">*</span></Label>
            <Select value={form.clinica_origen || ''} onValueChange={cambiarClinica}>
              <SelectTrigger data-testid="parte-clinica">
                <SelectValue placeholder="Elige la clínica o «Ninguna»" />
              </SelectTrigger>
              <SelectContent>
                {clinicas.map((c) => (
                  <SelectItem key={c.id || c.nombre} value={c.nombre}>{c.nombre}</SelectItem>
                ))}
                {clinicaFueraDeLista && (
                  <SelectItem value={form.clinica_origen}>{form.clinica_origen} (no está en la lista)</SelectItem>
                )}
                <SelectItem value={CLINICA_NINGUNA} data-testid="parte-clinica-ninguna">
                  Ninguna (no viene derivado de ninguna clínica)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Obligatoria. Solo clínicas de la lista (se gestionan en el Catálogo QX) o «Ninguna».
              {clinicaFueraDeLista ? <span className="text-amber-700"> «{form.clinica_origen}» no está en la lista: elige una para poder guardar.</span> : null}
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>Cirujano <span className="text-red-600">*</span></Label>
            {otroActivo ? (
              <Input
                value={form.cirujano}
                onChange={(e) => setF('cirujano', e.target.value)}
                placeholder={`Nombre del otro cirujano de ${form.clinica_origen}`}
                data-testid="parte-cirujano-otro-nombre"
                autoFocus
              />
            ) : (
              <CatalogCombobox
                tipo="cirujano"
                value={form.cirujano}
                onChange={(nombre, entry) => {
                  setF('cirujano', nombre);
                  if (entry?.clinica && (enLista(entry.clinica) || _norm(entry.clinica) === _norm(CLINICA_NINGUNA))) {
                    setF('clinica_origen', entry.clinica);
                  }
                }}
                placeholder="Elige el cirujano de la lista"
                clinicaParaNuevo={form.clinica_origen && !clinicaFueraDeLista && _norm(form.clinica_origen) !== _norm(CLINICA_NINGUNA) ? form.clinica_origen : undefined}
                dataTestid="parte-cirujano"
              />
            )}
            {conOtro && (
              <label className="mt-1.5 flex items-center gap-2 text-xs cursor-pointer" htmlFor="parte-cirujano-otro">
                <Checkbox
                  id="parte-cirujano-otro"
                  checked={form.cirujano_otro}
                  onCheckedChange={cambiarOtro}
                  data-testid="parte-cirujano-otro"
                />
                Otro cirujano de {form.clinica_origen} (no está en la lista): escribir su nombre
              </label>
            )}
            {otroActivo ? (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="parte-cirujano-externo-hint">
                Obligatorio. Se marca como <strong>externo</strong> automáticamente: sus intervenciones salen en el Registro de externos.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-1">
                Obligatorio. Solo cirujanos dados de alta; si falta alguno, dalo de alta desde el desplegable (Dr./Dra. Nombre y dos apellidos + colegiado).
                {fichaCirujano && (
                  <span className="block mt-0.5" data-testid="parte-cirujano-externo-hint">
                    {fichaCirujano.externo
                      ? <span className="text-[#2E6E5E]">Cirujano externo: sus intervenciones salen en el Registro de externos.</span>
                      : <span>Colaborador de la clínica (no externo): no sale en el Registro de externos.</span>}
                  </span>
                )}
              </p>
            )}
          </div>
          <div>
            <Label>Lugar de la operación <span className="text-red-600">*</span></Label>
            <CatalogCombobox
              tipo="lugar"
              value={form.lugar}
              onChange={(nombre) => setF('lugar', nombre)}
              placeholder="Dónde se opera (clínica u hospital)"
              dataTestid="parte-lugar"
            />
          </div>
          <div>
            <Label>Anestesista <span className="text-red-600">*</span></Label>
            <CatalogCombobox
              tipo="anestesista"
              value={form.anestesista}
              onChange={(nombre) => setF('anestesista', nombre)}
              placeholder="Selecciona anestesista"
              dataTestid="parte-anestesista"
            />
          </div>
          <div>
            <Label>Instrumentista <span className="text-red-600">*</span></Label>
            <CatalogCombobox
              tipo="instrumentista"
              value={form.instrumentista}
              onChange={(nombre) => setF('instrumentista', nombre)}
              placeholder="Selecciona instrumentista"
              dataTestid="parte-instrumentista"
            />
          </div>
          <div>
            <Label>Circulante <span className="text-red-600">*</span></Label>
            <CatalogCombobox
              tipo="circulante"
              value={form.circulante}
              onChange={(nombre) => setF('circulante', nombre)}
              placeholder="Selecciona circulante"
              dataTestid="parte-circulante"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Notas del parte (lo único opcional)</Label>
            <Input
              value={form.notas}
              onChange={(e) => setF('notas', e.target.value)}
              placeholder="Opcional"
              data-testid="parte-notas"
            />
          </div>
          <div className="md:col-span-2">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="parte-guardar">
                {isEdit ? 'Guardar cambios' : 'Crear parte'}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
